import { type KeyboardEvent, useRef, useState } from 'react'

import { cn } from '@/utils'
import { EditMark } from './EditMark'
import { FIELD_AT_REST, FIELD_EMPTY, FIELD_OPEN } from './fieldBox'
import { InlineFieldHint } from './InlineFieldHint'

interface InlineDateProps {
  // The stored day, as yyyy-MM-dd.
  value: string
  shown: string
  placeholder: string
  title: string
  className?: string
  onCommit: (value: string) => void
}

// InlineText for a day: text until it is clicked, then a date box in the same spot.
export const InlineDate = ({
  value,
  shown,
  placeholder,
  title,
  className,
  onCommit,
}: InlineDateProps) => {
  const [draft, setDraft] = useState<string | null>(null)
  const dropped = useRef(false)

  if (draft === null) {
    return (
      <button
        type="button"
        title={title}
        onClick={() => setDraft(value)}
        className={cn(FIELD_AT_REST, className, !value && FIELD_EMPTY)}
      >
        <span className="min-w-0">{shown || placeholder}</span>
        <EditMark />
      </button>
    )
  }

  // A half-typed date reads as empty. Letting go of one cancels instead of erasing the saved day.
  const commit = (isHalfTyped: boolean) => {
    if (!dropped.current && !isHalfTyped && draft !== value) onCommit(draft)
    dropped.current = false
    setDraft(null)
  }

  const shortcuts = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') event.currentTarget.blur()
    if (event.key !== 'Escape') return
    dropped.current = true
    event.currentTarget.blur()
  }

  return (
    <span className="flex w-full min-w-0 flex-col items-start">
      <input
        type="date"
        // The box only exists because the text was just clicked, so the caret goes straight in.
        ref={(box) => box?.focus()}
        value={draft}
        title={title}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={(event) => commit(event.currentTarget.validity.badInput)}
        onKeyDown={shortcuts}
        className={cn(FIELD_OPEN, className)}
      />
      <InlineFieldHint />
    </span>
  )
}
